import React, { useState, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import axios from 'axios';
import {
  ShoppingCart as SalesIcon,
  AddShoppingCart as PurchaseIcon,
  SwapHoriz as ReturnIcon,
  RequestQuote as QuoteIcon,
  Inventory as InventoryIcon,
  ImportExport as ImportIcon,
  Settings as SettingsIcon,
  Print as PrintIcon,
  QrCode as BarcodeIcon,
  Assessment as ReportsIcon
} from '@mui/icons-material';
import * as invoiceService from '../../services/invoiceService';
import { IInvoicePage } from '../../types/invoice';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';

// مكون قائمة الفواتير الديناميكية
// هذا المكون سيتم استدعاؤه من SidebarMenu.tsx
export const getInvoiceMenuItems = async () => {
  try {
    // محاولة الحصول على معلومات المستخدم للتحقق مما إذا كان مدير نظام
    const userInfoStr = localStorage.getItem('user');
    let isSystemAdmin = false;
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        isSystemAdmin = userInfo?.isSystemAdmin;
      } catch (e) {
        console.error('خطأ في تحليل معلومات المستخدم:', e);
      }
    }
    
    // محاولة الحصول على صفحات الفواتير من الخادم
    let pages: IInvoicePage[] = [];
    
    try {
      pages = await invoiceService.getInvoicePages();
    } catch (apiError: any) {
      // إذا فشل الطلب وكان المستخدم مديرًا، نستخدم قائمة ثابتة
      if (isSystemAdmin && apiError.response?.status === 403) {
        console.log("[DynamicInvoiceMenu] فشل في الوصول إلى API صفحات الفواتير، استخدام قائمة ثابتة للمدير");
        
        // قائمة ثابتة للمدير تتضمن جميع أنواع الفواتير الأساسية
        pages = [
          { _id: "sales-invoice", sequentialId: 1, name: "فواتير المبيعات", type: "sales_invoice" } as IInvoicePage,
          { _id: "purchase-invoice", sequentialId: 2, name: "فواتير المشتريات", type: "purchase_invoice" } as IInvoicePage,
          { _id: "sales-return", sequentialId: 3, name: "مرتجعات المبيعات", type: "sales_return" } as IInvoicePage,
          { _id: "purchase-return", sequentialId: 4, name: "مرتجعات المشتريات", type: "purchase_return" } as IInvoicePage,
          { _id: "quotation", sequentialId: 5, name: "عروض الأسعار", type: "quotation" } as IInvoicePage,
          { _id: "beginning-inventory", sequentialId: 6, name: "جرد بداية", type: "beginning_inventory" } as IInvoicePage
        ];
      } else {
        // إذا لم يكن المستخدم مديرًا أو كان الخطأ ليس 403، إعادة رمي الخطأ
        throw apiError;
      }
    }
    
    // الحصول على العناصر الأساسية للفواتير
    const invoiceItems = pages.map(page => ({
      id: page._id,
      to: `/invoices/${page._id}`,
      icon: getIconForInvoiceType(page.type),
      text: page.name || getArabicNameForType(page.type),
      permissionCheck: {
        section: 'invoices',
        page: `page-${page.sequentialId}`,
        action: 'view_page' // صلاحية الوصول للصفحة فقط، يتم التحقق من صلاحيات العرض والتعديل في الصفحات الداخلية
      }
    }));
    
    // إضافة العناصر الثابتة للإعدادات
    const staticItems = [
      {
        id: 'invoice-reports-summary',
        to: '/invoices/reports/summary',
        icon: <ReportsIcon fontSize="small" />,
        text: 'ملخص الفواتير',
        permissionCheck: {
          section: 'invoices',
          page: 'reports',
          action: 'view_page'
        }
      },
      {
        id: 'invoice-settings',
        to: '/invoices/settings',
        icon: <SettingsIcon fontSize="small" />,
        text: 'إعدادات الفواتير',
        permissionCheck: {
          section: 'invoices',
          page: 'settings',
          action: 'view_page'
        }
      },
      {
        id: 'print-templates',
        to: '/invoices/print-templates',
        icon: <PrintIcon fontSize="small" />,
        text: 'قوالب الطباعة',
        permissionCheck: {
          section: 'invoices',
          page: 'print-templates',
          action: 'view_page'
        }
      },
      {
        id: 'product-barcode',
        to: '/invoices/barcode-print',
        icon: <BarcodeIcon fontSize="small" />,
        text: 'طباعة باركود المنتجات',
        permissionCheck: {
          section: 'invoices',
          page: 'product-barcode',
          action: 'view_page'
        }
      }
    ];
    
    return [...invoiceItems, ...staticItems];
  } catch (err) {
    console.error('خطأ في تحميل صفحات الفواتير:', err);
    return [];
  }
};

// اختيار الأيقونة المناسبة لنوع الفاتورة
export const getIconForInvoiceType = (type: string) => {
  switch (type) {
    case 'sales_invoice':
      return <SalesIcon fontSize="small" />;
    case 'purchase_invoice':
      return <PurchaseIcon fontSize="small" />;
    case 'sales_return':
    case 'purchase_return':
      return <ReturnIcon fontSize="small" />;
    case 'quotation':
      return <QuoteIcon fontSize="small" />;
    case 'beginning_inventory':
      return <InventoryIcon fontSize="small" />;
    case 'import_invoice_return':
      return <ImportIcon fontSize="small" />;
    default:
      return <SalesIcon fontSize="small" />;
  }
};

// الحصول على اسم عربي لنوع الفاتورة
export const getArabicNameForType = (type: string) => {
  switch (type) {
    case 'sales_invoice':
      return 'فواتير المبيعات';
    case 'purchase_invoice':
      return 'فواتير المشتريات';
    case 'sales_return':
      return 'مرتجعات المبيعات';
    case 'purchase_return':
      return 'مرتجعات المشتريات';
    case 'quotation':
      return 'عروض الأسعار';
    case 'beginning_inventory':
      return 'جرد بداية';
    case 'import_invoice_return':
      return 'استيراد مرتجعات الفواتير';
    default:
      return type;
  }
};